'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Button, CodeInput, FormField, ON_GLASS, ON_GLASS_SUBTLE } from '@spark/ui';
import { mapAuthError } from '@/lib/auth/errors';
import { AUTH_TOAST_STYLE, AuthLink } from '@/components/auth/AdminAuthHeroLayout';
import { ROUTES, sanitizeRedirectAfter } from '@/lib/routes';
import { bffFetch } from '@/lib/api/bffFetch';

const RESEND_COOLDOWN_SECONDS = 60;
const PRE_MFA_SID_KEY = 'spark_admin_pre_mfa_sid';
const PRE_MFA_REDIRECT_KEY = 'spark_admin_pre_mfa_redirect_after';

function redactEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const head = local.slice(0, 1);
  return `${head}${'*'.repeat(Math.max(local.length - 1, 1))}@${domain}`;
}

export function SignInVerifyForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const identity = searchParams.get('identity') ?? '';
  const redirectAfter = sanitizeRedirectAfter(searchParams.get('redirectAfter'));
  const redacted = identity ? redactEmail(identity) : 'your email';

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [toast, setToast] = useState<string | undefined>();
  const [resendCooldown, setResendCooldown] = useState(0);
  const [inputKey, setInputKey] = useState(0);
  const resendTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Synchronous re-entrancy guard: auto-submit (on the 6th digit) and a stray
  // Enter could both fire before `submitting` state commits, so guard with a ref.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    resendTimer.current = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => {
      if (resendTimer.current) clearInterval(resendTimer.current);
    };
  }, [resendCooldown]);

  function resetCodeInput() {
    // Clear the boxes and remount CodeInput (bumped key) so focus snaps back to
    // the first box — lets the user retype immediately after a failed attempt
    // without manually deleting each digit.
    setCode('');
    setInputKey((k) => k + 1);
  }

  async function submitCode(codeToSubmit: string) {
    if (submittingRef.current || codeToSubmit.length !== 6) return;
    submittingRef.current = true;
    setSubmitting(true);
    setErrors({});
    setToast(undefined);

    try {
      const res = await bffFetch('/api/v1/public/auth/code-verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ emailOrPhone: identity, code: codeToSubmit }),
      });

      if (res.ok) {
        const body = (await safeJson(res)) as
          | {
              kind?: 'TOTP_REQUIRED' | 'AUTHENTICATED' | 'INVALID_CODE' | 'CODE_EXPIRED';
              // API returns preMfaToken (JWT). Stored as preMfaSid in sessionStorage;
              // the TOTP proxy forwards it as the Authorization Bearer to apps/api.
              preMfaToken?: string;
            }
          | null;
        if (body?.kind === 'TOTP_REQUIRED' && body.preMfaToken) {
          // Per spec §8: pre-MFA JWT stored in a short-lived sessionStorage entry,
          // cleared on success/fail/back. The TOTP form reads it on mount.
          try {
            sessionStorage.setItem(PRE_MFA_SID_KEY, body.preMfaToken);
            sessionStorage.setItem(PRE_MFA_REDIRECT_KEY, redirectAfter);
          } catch {
            // SessionStorage may be disabled (private browsing); fall back to query param.
          }
          router.push(ROUTES.signInTotp);
          return;
        }
        if (body?.kind === 'AUTHENTICATED') {
          // Admin pool should never authenticate without MFA, but if upstream returns
          // AUTHENTICATED for a tester account that bypassed MFA, navigate to /home.
          router.push(redirectAfter || ROUTES.home);
          return;
        }
        if (body?.kind === 'INVALID_CODE') {
          setErrors({ code: 'That code didn’t match. Try again.' });
          resetCodeInput();
          return;
        }
        if (body?.kind === 'CODE_EXPIRED') {
          setErrors({ code: 'That code expired. Request a new one.' });
          resetCodeInput();
          return;
        }
        // Unknown kind — treat as toast-level error.
        setToast('Unexpected response. Please request a new code.');
        resetCodeInput();
        return;
      }

      const body = await safeJson(res);
      const mapped = mapAuthError(body, res.status);
      if (mapped.inline) setErrors({ [mapped.inline.field ?? 'code']: mapped.inline.message });
      if (mapped.toast) setToast(mapped.toast.message);
      resetCodeInput();
    } catch {
      setToast('Something went wrong. Please try again.');
      resetCodeInput();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submitCode(code);
  }

  async function onResend() {
    if (resendCooldown > 0 || !identity) return;
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    try {
      await bffFetch('/api/v1/public/auth/code-request', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ emailOrPhone: identity, channel: 'EMAIL' }),
      });
    } catch {
      // Silent — cooldown already applied so we don't hammer upstream.
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate aria-busy={submitting || undefined}>
      <p className="text-body" style={{ color: ON_GLASS }}>
        We sent a 6-digit code to {redacted}.
      </p>

      <FormField label="Code" required errorText={errors.code} tone="on-dark">
        <CodeInput
          key={inputKey}
          tone="on-dark"
          value={code}
          onChange={setCode}
          onComplete={(value) => void submitCode(value)}
          autoFocus
          invalid={Boolean(errors.code)}
          disabled={submitting}
          ariaLabel="6-digit sign-in code"
        />
      </FormField>

      {/* The code submits automatically on the 6th digit — no Verify button. */}
      <p className="min-h-[1.25rem] text-body-sm" style={{ color: ON_GLASS_SUBTLE }} role="status" aria-live="polite">
        {submitting ? 'Verifying…' : ''}
      </p>

      {toast ? (
        <div role="alert" className="rounded-xl px-3 py-2 text-body-sm" style={AUTH_TOAST_STYLE}>
          {toast}
        </div>
      ) : null}

      <div className="flex items-center justify-between text-body-sm">
        <Button
          type="button"
          variant="ghost-on-dark"
          size="sm"
          onClick={onResend}
          disabled={resendCooldown > 0 || !identity}
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
        </Button>
        <AuthLink href={ROUTES.signIn}>Use a different email</AuthLink>
      </div>
    </form>
  );
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
